import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './dialog'
import { LogoMark } from './logo'
import { getGoogleIdAsync, handleGoogleCredential } from '@/lib/auth/google'
import { startGithubLogin } from '@/lib/auth/github'

interface LoginDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onSuccess: () => void
}

export function LoginDialog({ open, onClose, onSuccess }: LoginDialogProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return
    if (!container) return

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) {
      setError(t('auth.googleClientIdMissing'))
      return
    }

    let cancelled = false
    setError(null)

    ;(async () => {
      const google = await getGoogleIdAsync()
      if (cancelled) return
      if (!google) {
        setError(t('auth.googleSdkFailed'))
        return
      }

      google.cancel()

      google.initialize({
        client_id: clientId,
        callback: async (response) => {
          const result = await handleGoogleCredential(response.credential)
          if (cancelled) return
          if (!result.ok) {
            setError(result.error)
            return
          }
          onSuccess()
        },
      })

      container.innerHTML = ''
      google.renderButton(container, {
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'signin_with',
        width: 320,
      })
    })()

    return () => { cancelled = true }
  }, [open, container, onSuccess, t])

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mb-2 flex justify-center">
            <LogoMark size={36} />
          </div>
          <DialogTitle className="text-center">{t('auth.signInToContinue')}</DialogTitle>
          <DialogDescription className="text-center">
            {t('auth.signInToStartResearch')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-[44px] justify-center">
          <div ref={setContainer} />
        </div>

        <div className="my-2 flex items-center gap-3 text-xs uppercase text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          <span>{t('auth.or')}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <button
          type="button"
          onClick={() => {
            const returnTo = window.location.pathname + window.location.search
            const r = startGithubLogin(returnTo)
            if (!r.ok) setError(r.error)
          }}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            className="fill-current"
          >
            <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.54-3.88-1.54-.52-1.32-1.27-1.67-1.27-1.67-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.75 2.69 1.25 3.35.95.1-.74.4-1.25.73-1.54-2.55-.29-5.24-1.27-5.24-5.66 0-1.25.45-2.27 1.18-3.07-.12-.29-.51-1.46.11-3.04 0 0 .96-.31 3.15 1.18a10.94 10.94 0 0 1 5.72 0c2.19-1.49 3.15-1.18 3.15-1.18.62 1.58.23 2.75.11 3.04.74.8 1.18 1.82 1.18 3.07 0 4.4-2.69 5.36-5.25 5.65.41.36.78 1.06.78 2.14v3.17c0 .31.21.67.8.56C20.21 21.39 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5Z" />
          </svg>
          {t('auth.signInWithGithub')}
        </button>

        {error && (
          <p className="text-center text-sm text-destructive">{error}</p>
        )}

        <p className="text-center text-xs text-muted-foreground">
          {t('auth.privacyNotice')}
        </p>
      </DialogContent>
    </Dialog>
  )
}
